// pages/eventos/admin/EditarEventoPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  Gift,
  Plus,
  Save,
  Settings2,
  Trash2,
} from "lucide-react";

import axios from "../../../axiosConfig";
import "./EditarEventoPage.css";

const EditarEventoPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [evento, setEvento] = useState(null);
  const [premios, setPremios] = useState([]);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    const carregarEvento = async () => {
      try {
        const res = await axios.get(`/admin/eventos/${slug}`);
        const { evento, premios } = res.data;

        setEvento({
          ...evento,
          data_inicio: evento.data_inicio?.split("T")[0],
          data_fim: evento.data_fim?.split("T")[0],
        });

        setPremios(Array.isArray(premios) ? premios : []);
      } catch {
        setErro("Erro ao carregar dados do evento.");
      }
    };

    carregarEvento();
  }, [slug]);

  const totalPremios = useMemo(
    () => premios.reduce((sum, p) => sum + Number(p.quantidade || 0), 0),
    [premios]
  );

  const participantesMaximos = Number(evento?.limite_participantes || 0);
  const premiosInsuficientes =
    participantesMaximos > 0 && totalPremios < participantesMaximos;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setEvento((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePremioChange = (index, field, value) => {
    const atualizados = [...premios];
    atualizados[index][field] = value;
    setPremios(atualizados);
  };

  const handleAddPremio = () => {
    setPremios((prev) => [
      ...prev,
      {
        tipo: "pontos",
        quantidade: 1,
        qtd_pontos: "",
        premio_id: "",
        sortear_ate_participante: "",
      },
    ]);
  };

  const handleRemovePremio = (index) => {
    setPremios((prev) => prev.filter((_, i) => i !== index));
  };

  const validarPremios = () => {
    if (premios.length === 0) return "Adicione ao menos um prêmio.";

    for (const p of premios) {
      if (
        !p.quantidade ||
        !p.sortear_ate_participante ||
        (p.tipo === "pontos" && !p.qtd_pontos) ||
        (p.tipo === "fisico" && !p.premio_id)
      ) {
        return "Preencha todos os campos de prêmios corretamente.";
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setMensagem("");

    const erroValidacao = validarPremios();

    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    setEnviando(true);

    try {
      await axios.put(`/admin/eventos/${slug}`, { evento, premios });
      setMensagem("Evento atualizado com sucesso!");
      setTimeout(() => navigate("/admin/eventos"), 1500);
    } catch (err) {
      console.error(err);
      setErro("Erro ao atualizar evento.");
    } finally {
      setEnviando(false);
    }
  };

  if (!evento) {
    return (
      <div className="editarEventoAdmin-page">
        <div className="editarEventoAdmin-loading">Carregando evento...</div>
      </div>
    );
  }

  return (
    <div className="editarEventoAdmin-page">
      <div className="editarEventoAdmin-topbar">
        <div>
          <span className="editarEventoAdmin-kicker">Eventos promocionais</span>
          <h2>Editar Evento</h2>
          <p>
            Atualize os dados do evento e ajuste a composição dos prêmios sem
            alterar a lógica de sorteio já configurada no backend.
          </p>
        </div>

        <button
          type="button"
          className="editarEventoAdmin-btn secondary"
          onClick={() => navigate("/admin/eventos")}
        >
          <ArrowLeft size={16} />
          Voltar
        </button>
      </div>

      {mensagem && (
        <div className="editarEventoAdmin-alert success">{mensagem}</div>
      )}

      {erro && <div className="editarEventoAdmin-alert error">{erro}</div>}

      <form className="editarEventoAdmin-form" onSubmit={handleSubmit}>
        <section className="editarEventoAdmin-card">
          <div className="editarEventoAdmin-cardHeader">
            <div>
              <h3>Dados do evento</h3>
              <span>Informações principais, período e status de publicação.</span>
            </div>

            <CalendarDays size={18} />
          </div>

          <div className="editarEventoAdmin-grid">
            <div className="editarEventoAdmin-field">
              <label>Slug</label>
              <input value={evento.slug || ""} disabled />
            </div>

            <div className="editarEventoAdmin-field">
              <label>Nome do evento</label>
              <input
                name="nome_evento"
                value={evento.nome_evento || ""}
                onChange={handleChange}
                required
              />
            </div>

            <div className="editarEventoAdmin-field">
              <label>Data início</label>
              <input
                type="date"
                name="data_inicio"
                value={evento.data_inicio || ""}
                onChange={handleChange}
              />
            </div>

            <div className="editarEventoAdmin-field">
              <label>Data fim</label>
              <input
                type="date"
                name="data_fim"
                value={evento.data_fim || ""}
                onChange={handleChange}
              />
            </div>

            <div className="editarEventoAdmin-field">
              <label>Participantes máximos</label>
              <input
                type="number"
                name="limite_participantes"
                value={evento.limite_participantes || ""}
                onChange={handleChange}
              />
            </div>

            <div className="editarEventoAdmin-field editarEventoAdmin-statusField">
              <label>Status</label>

              <label className="editarEventoAdmin-toggle">
                <input
                  type="checkbox"
                  name="ativo"
                  checked={Boolean(evento.ativo)}
                  onChange={handleChange}
                />
                <span />
                <strong>{evento.ativo ? "Evento ativo" : "Evento inativo"}</strong>
              </label>
            </div>

            <div className="editarEventoAdmin-field editarEventoAdmin-full">
              <label>Descrição</label>
              <textarea
                name="descricao"
                value={evento.descricao || ""}
                onChange={handleChange}
                rows={3}
              />
            </div>
          </div>
        </section>

        <section className="editarEventoAdmin-card">
          <div className="editarEventoAdmin-cardHeader">
            <div>
              <h3>Prêmios do evento</h3>
              <span>
                Configure quantidade, tipo de prêmio e até qual participante cada
                prêmio poderá ser sorteado.
              </span>
            </div>

            <Gift size={18} />
          </div>

          <div className="editarEventoAdmin-premiosIntro">
            <Settings2 size={16} />
            <p>
              O total de prêmios deve ser igual ou superior ao número de
              participantes. Use o campo “disponível até” para limitar prêmios
              especiais aos primeiros sorteios.
            </p>
          </div>

          <div className="editarEventoAdmin-premiosLista">
            {premios.map((p, index) => (
              <div key={index} className="editarEventoAdmin-premioLinha">
                <div className="editarEventoAdmin-field">
                  <label>Tipo de prêmio</label>
                  <select
                    value={p.tipo || "pontos"}
                    onChange={(e) =>
                      handlePremioChange(index, "tipo", e.target.value)
                    }
                  >
                    <option value="pontos">Pontos</option>
                    <option value="fisico">Físico</option>
                  </select>
                </div>

                <div className="editarEventoAdmin-field">
                  <label>Quantidade</label>
                  <input
                    type="number"
                    placeholder="Ex: 3"
                    value={p.quantidade || ""}
                    onChange={(e) =>
                      handlePremioChange(index, "quantidade", e.target.value)
                    }
                  />
                </div>

                {p.tipo === "pontos" ? (
                  <div className="editarEventoAdmin-field">
                    <label>Quantidade de pontos</label>
                    <input
                      type="number"
                      placeholder="Ex: 500"
                      value={p.qtd_pontos || ""}
                      onChange={(e) =>
                        handlePremioChange(index, "qtd_pontos", e.target.value)
                      }
                    />
                  </div>
                ) : (
                  <div className="editarEventoAdmin-field">
                    <label>ID do prêmio físico</label>
                    <input
                      type="number"
                      placeholder="Ex: 164"
                      value={p.premio_id || ""}
                      onChange={(e) =>
                        handlePremioChange(index, "premio_id", e.target.value)
                      }
                    />
                  </div>
                )}

                <div className="editarEventoAdmin-field">
                  <label>Disponível até participante nº</label>
                  <input
                    type="number"
                    placeholder="Ex: 10"
                    value={p.sortear_ate_participante || ""}
                    onChange={(e) =>
                      handlePremioChange(
                        index,
                        "sortear_ate_participante",
                        e.target.value
                      )
                    }
                  />
                </div>

                <button
                  type="button"
                  className="editarEventoAdmin-removeBtn"
                  onClick={() => handleRemovePremio(index)}
                  title="Remover prêmio"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="editarEventoAdmin-btn secondary"
            onClick={handleAddPremio}
          >
            <Plus size={16} />
            Adicionar prêmio
          </button>

          <div
            className={`editarEventoAdmin-validacao ${
              premiosInsuficientes ? "warning" : "ok"
            }`}
          >
            <div>
              <span>Total de prêmios cadastrados</span>
              <strong>{totalPremios}</strong>
            </div>

            <div>
              <span>Participantes máximos definidos</span>
              <strong>{evento.limite_participantes || "—"}</strong>
            </div>

            {premiosInsuficientes && (
              <p>O total de prêmios é inferior ao número de participantes.</p>
            )}
          </div>
        </section>

        <div className="editarEventoAdmin-submitBar">
          <button
            type="button"
            className="editarEventoAdmin-btn secondary"
            onClick={() => navigate("/admin/eventos")}
          >
            Cancelar
          </button>

          <button
            type="submit"
            className="editarEventoAdmin-btn primary"
            disabled={enviando}
          >
            <Save size={16} />
            {enviando ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditarEventoPage;